import { ACTIVE_KYC_STEPS, getSessionStep, KYC_STEPS } from "./kycSteps";
import {
  createKycAttemptState,
  KYC_ATTEMPT_STAGES,
  KYC_ATTEMPT_STATUS,
  requiresSecondSubmission,
} from "./kycAttemptState";
import {
  documentRequiresBack,
  getAllowedDocumentTypes,
  getInitialDocumentType,
} from "../services/documentRequirements";
import {
  ADVANCED_VERIFICATION_CHECKS,
  createAdvancedVerificationState,
} from "../advanced-verification/AdvancedVerificationGateway";

const getSessionSource = (session) =>
  session?.personalInfo ||
  session?.applicant ||
  session?.orderDetail ||
  session?.order ||
  session ||
  {};

const getInitialPersonalInfo = (session) => {
  const source = getSessionSource(session);

  return {
    firstName: source.firstName || "",
    lastName: source.lastName || "",
    email: source.email || "",
    phone: source.phone || "",
    address: source.address || source.detailAddress || "",
    city: source.city || "",
    state: source.state || "",
    postalCode: source.postalCode || source.zipcode || "",
  };
};

const createDocumentImageState = (source = {}) => ({
  file: null,
  previewUrl: source.previewUrl || source.url || "",
  uploadedUrl: source.uploadedUrl || source.url || "",
  isPlaceholder: false,
});

const createEmptyDocumentState = () => ({
  front: createDocumentImageState(),
  back: createDocumentImageState(),
});

const hasSubmittedValue = (value) =>
  value !== undefined && value !== null && value !== "";

const mergeSubmittedOrder = (currentOrder, submittedOrder) =>
  Object.entries(submittedOrder || {}).reduce(
    (result, [field, value]) => {
      if (hasSubmittedValue(value)) result[field] = value;
      return result;
    },
    { ...currentOrder },
  );

export function createInitialKycState(session) {
  const source = getSessionSource(session);
  const country = session?.country || source.country || "";
  const documentSource = session?.document || {};
  const documentFrontSource = documentSource.front || {
    previewUrl: documentSource.previewUrl,
    url: documentSource.url || source.documentFront,
  };
  const documentBackSource = documentSource.back || {
    url: source.documentBack,
  };
  const selfieSource = session?.selfie || {};
  const livenessSource = session?.liveness || {};
  const addressProofSource =
    session?.addressProofDocument || source.addressProofDocument || {};
  const addressProofUrl =
    typeof addressProofSource === "string"
      ? addressProofSource
      : addressProofSource.url || addressProofSource.uploadedUrl || "";
  const documentType = getInitialDocumentType(
    country,
    session?.documentType || documentSource.type || source.identityType,
  );
  const sessionStep = getSessionStep(session);
  const documentEntrySteps = [
    KYC_STEPS.DOCUMENT_TYPE,
    KYC_STEPS.DOCUMENT_PREPARATION,
    KYC_STEPS.DOCUMENT_CAPTURE,
    KYC_STEPS.DOCUMENT_REVIEW,
    KYC_STEPS.DOCUMENT_BACK_PREPARATION,
  ];
  const currentStep =
    !documentType && documentEntrySteps.includes(sessionStep)
      ? KYC_STEPS.DOCUMENT_TYPE
      : sessionStep === KYC_STEPS.DOCUMENT_BACK_PREPARATION &&
          !documentRequiresBack(documentType)
        ? KYC_STEPS.SELFIE
        : sessionStep;

  return {
    session,
    currentStep,
    personalInfo: getInitialPersonalInfo(session),
    country,
    documentType,
    document: {
      front: createDocumentImageState(documentFrontSource),
      back: createDocumentImageState(documentBackSource),
    },
    capture: {
      status: "idle",
      detectionStatus: "SEARCHING",
      documentSide:
        documentRequiresBack(documentType) &&
        documentFrontSource.url &&
        !documentBackSource.url
          ? "back"
          : "front",
    },
    selfie: {
      file: null,
      previewUrl: selfieSource.previewUrl || selfieSource.url || "",
      uploadedUrl: selfieSource.url || "",
    },
    liveness: {
      value:
        livenessSource.url ||
        livenessSource.value ||
        source.userSelfPictureOrVideo ||
        "",
      completed: Boolean(
        livenessSource.completed ||
        livenessSource.url ||
        source.userSelfPictureOrVideo,
      ),
    },
    advancedVerification: createAdvancedVerificationState(),
    addressProof: {
      file: null,
      previewUrl: addressProofUrl,
      uploadedUrl: addressProofUrl,
      fileName:
        typeof addressProofSource === "object"
          ? addressProofSource.fileName || addressProofSource.name || ""
          : "",
      fileType:
        typeof addressProofSource === "object"
          ? addressProofSource.fileType || ""
          : "",
    },
    attempts: createKycAttemptState(),
    status: String(session?.status || "ready").toLowerCase(),
    submission: null,
    error: null,
  };
}

export function kycReducer(state, action) {
  switch (action.type) {
    case "HYDRATE_SESSION":
      return createInitialKycState(action.session);

    case "NEXT": {
      const currentIndex = ACTIVE_KYC_STEPS.indexOf(state.currentStep);
      const nextStep = ACTIVE_KYC_STEPS[currentIndex + 1];

      return nextStep
        ? { ...state, currentStep: nextStep, error: null }
        : state;
    }

    case "BACK": {
      const currentIndex = ACTIVE_KYC_STEPS.indexOf(state.currentStep);
      const previousStep = ACTIVE_KYC_STEPS[currentIndex - 1];

      return previousStep
        ? { ...state, currentStep: previousStep, error: null }
        : state;
    }

    case "GO_TO":
      return Object.values(KYC_STEPS).includes(action.step)
        ? { ...state, currentStep: action.step, error: null }
        : state;

    case "UPDATE_PERSONAL_INFO":
      return {
        ...state,
        personalInfo: { ...state.personalInfo, ...action.value },
        error: null,
      };

    case "SET_COUNTRY": {
      const allowedTypes = getAllowedDocumentTypes(action.value);
      return {
        ...state,
        country: action.value,
        documentType: allowedTypes[0],
        document: createEmptyDocumentState(),
        capture: {
          status: "idle",
          detectionStatus: "SEARCHING",
          documentSide: "front",
        },
        advancedVerification: {
          ...state.advancedVerification,
          [ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY]:
            createAdvancedVerificationState()[
              ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY
            ],
        },
        error: null,
      };
    }

    case "SET_DOCUMENT_TYPE":
      return {
        ...state,
        documentType: action.value,
        document: createEmptyDocumentState(),
        capture: {
          status: "idle",
          detectionStatus: "SEARCHING",
          documentSide: "front",
        },
        advancedVerification: {
          ...state.advancedVerification,
          [ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY]:
            createAdvancedVerificationState()[
              ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY
            ],
        },
        error: null,
      };

    case "SET_CAPTURE":
      return { ...state, capture: { ...state.capture, ...action.value } };

    // 相机每次只返回一面；按 capture.documentSide 合并，避免覆盖另一面数据。
    case "SET_DOCUMENT": {
      const documentSide = action.side || state.capture.documentSide || "front";
      return {
        ...state,
        document: {
          ...state.document,
          [documentSide]: {
            ...state.document[documentSide],
            ...action.value,
          },
        },
        error: null,
      };
    }

    // 单面重拍：只清理当前面并回到相机，另一面保持不变。
    case "RESET_DOCUMENT": {
      const resetSide = action.side || state.capture.documentSide || "front";

      return {
        ...state,
        document: {
          ...state.document,
          [resetSide]: createDocumentImageState(),
        },
        capture: {
          status: "idle",
          detectionStatus: "SEARCHING",
          documentSide: resetSide,
        },
        advancedVerification: {
          ...state.advancedVerification,
          [ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY]:
            createAdvancedVerificationState()[
              ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY
            ],
        },
        currentStep: KYC_STEPS.DOCUMENT_CAPTURE,
        error: null,
      };
    }

    // Restart the full document stage and show its guidance before the camera.
    case "RESTART_DOCUMENT_CAPTURE":
      return {
        ...state,
        document: createEmptyDocumentState(),
        capture: {
          status: "idle",
          detectionStatus: "SEARCHING",
          documentSide: "front",
        },
        advancedVerification: {
          ...state.advancedVerification,
          [ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY]:
            createAdvancedVerificationState()[
              ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY
            ],
        },
        currentStep: KYC_STEPS.DOCUMENT_PREPARATION,
        error: null,
      };

    case "SET_SELFIE":
      return {
        ...state,
        selfie: { ...state.selfie, ...action.value },
        error: null,
      };

    case "SET_LIVENESS":
      return {
        ...state,
        liveness: { ...state.liveness, ...action.value },
        error: null,
      };

    case "SET_ADVANCED_VERIFICATION":
      return Object.values(ADVANCED_VERIFICATION_CHECKS).includes(action.check)
        ? {
            ...state,
            advancedVerification: {
              ...state.advancedVerification,
              [action.check]: action.value,
            },
          }
        : state;

    case "SET_ADDRESS_PROOF":
      return {
        ...state,
        addressProof: { ...state.addressProof, ...action.value },
        error: null,
      };

    case "ATTEMPT_SUBMIT_START": {
      const attempt = state.attempts[action.stage];
      if (!attempt) return state;

      return {
        ...state,
        attempts: {
          ...state.attempts,
          [action.stage]: {
            ...attempt,
            status:
              requiresSecondSubmission(action.stage) &&
              attempt.completedAttempts >= 1
                ? KYC_ATTEMPT_STATUS.SUBMIT_ATTEMPT_2
                : KYC_ATTEMPT_STATUS.SUBMIT_ATTEMPT_1,
          },
        },
        error: null,
      };
    }

    case "ATTEMPT_FIRST_FAILURE": {
      const attempt = state.attempts[action.stage];
      if (!attempt || !requiresSecondSubmission(action.stage)) return state;

      const nextState = {
        ...state,
        attempts: {
          ...state.attempts,
          [action.stage]: {
            completedAttempts: 1,
            status: KYC_ATTEMPT_STATUS.FAILED_FIRST_ATTEMPT,
          },
        },
        error: null,
      };

      if (action.stage === KYC_ATTEMPT_STAGES.FACE) {
        return {
          ...nextState,
          selfie: { file: null, previewUrl: "", uploadedUrl: "" },
          liveness: { value: "", completed: false },
          advancedVerification: {
            ...state.advancedVerification,
            [ADVANCED_VERIFICATION_CHECKS.BIOMETRIC_LIVENESS]:
              createAdvancedVerificationState()[
                ADVANCED_VERIFICATION_CHECKS.BIOMETRIC_LIVENESS
              ],
          },
          currentStep: KYC_STEPS.LIVENESS,
        };
      }

      return {
        ...nextState,
        addressProof: {
          file: null,
          previewUrl: "",
          uploadedUrl: "",
          fileName: "",
          fileType: "",
        },
        currentStep: KYC_STEPS.ADDRESS_PROOF,
      };
    }

    case "ATTEMPT_RETRY": {
      const attempt = state.attempts[action.stage];
      if (!attempt) return state;

      return {
        ...state,
        attempts: {
          ...state.attempts,
          [action.stage]: {
            ...attempt,
            status: KYC_ATTEMPT_STATUS.CAPTURE,
          },
        },
        error: null,
      };
    }

    case "ATTEMPT_COMPLETE": {
      const attempt = state.attempts[action.stage];
      if (!attempt) return state;

      return {
        ...state,
        attempts: {
          ...state.attempts,
          [action.stage]: {
            completedAttempts: requiresSecondSubmission(action.stage) ? 2 : 1,
            status: KYC_ATTEMPT_STATUS.COMPLETED,
          },
        },
        error: null,
      };
    }

    case "ATTEMPT_SUBMIT_ERROR": {
      const attempt = state.attempts[action.stage];
      if (!attempt) return state;

      return {
        ...state,
        attempts: {
          ...state.attempts,
          [action.stage]: {
            ...attempt,
            status: KYC_ATTEMPT_STATUS.CAPTURE,
          },
        },
        error: action.error || null,
      };
    }

    case "SYNC_LEGACY_ORDER": {
      const currentSession = state.session || {};
      const currentOrder =
        currentSession.orderDetail ||
        currentSession.order ||
        currentSession.kycData ||
        {};

      return {
        ...state,
        session: {
          ...currentSession,
          orderDetail: mergeSubmittedOrder(currentOrder, action.value),
        },
      };
    }

    case "SYNC_SESSION":
      return {
        ...state,
        session: { ...(state.session || {}), ...(action.session || {}) },
      };

    case "SUBMIT_START":
      return {
        ...state,
        currentStep: KYC_STEPS.SUBMITTING,
        status: "submitting",
        error: null,
      };

    case "SUBMIT_PROCESSING":
      return {
        ...state,
        currentStep: KYC_STEPS.PROCESSING,
        status: "processing",
        submission: action.value || null,
        error: null,
      };

    case "SUBMIT_SUCCESS":
      return {
        ...state,
        currentStep: KYC_STEPS.SUCCESS,
        status: "success",
        submission: action.value || state.submission,
        error: null,
      };

    case "SUBMIT_FAILURE":
      return {
        ...state,
        currentStep: KYC_STEPS.FAILED,
        status: "failed",
        error: action.error || null,
      };

    case "SESSION_EXPIRED":
      return {
        ...state,
        currentStep: KYC_STEPS.EXPIRED,
        status: "expired",
        error: null,
      };

    case "RETRY":
      return {
        ...state,
        currentStep: action.step || KYC_STEPS.PROCESSING,
        status: "ready",
        error: null,
      };

    case "SET_ERROR":
      return { ...state, error: action.error };

    case "CLEAR_ERROR":
      return { ...state, error: null };

    default:
      return state;
  }
}
